'use client';

// Bọc 1 lời gọi bất đồng bộ (thường Firestore `getDocs`/`getDoc` — SDK KHÔNG nhận `AbortSignal` như
// `fetch`) bằng TIMEOUT THỦ CÔNG (`Promise.race`) + TỰ THỬ LẠI khi mạng treo/mất kết nối. Cùng
// nguyên tắc với `lib/auth.tsx` `syncUserProfile` (đã có `AbortSignal.timeout` cho `fetch`, xem
// docs/perf-boot.md §1.1) nhưng generic hơn: dùng cho MỌI Promise, không riêng `fetch`.
//
// ⚠️ Race chỉ NGỪNG CHỜ chứ KHÔNG HUỶ được lời gọi gốc (Firestore SDK không hỗ trợ huỷ `getDocs`) —
// lời gọi gốc vẫn chạy ngầm tới khi tự xong hoặc tự lỗi; kết quả TRỄ (về sau khi đã timeout/retry)
// phải được nơi gọi tự bỏ qua bằng cờ `cancelled` (cùng pattern đã dùng khắp AppShell/auth.tsx).
//
// Bug thật dẫn tới việc này (2026-09-23, tenant Nắng Home, 5G/wifi yếu): `BuildingProvider` gọi
// `getDocs(buildings)` không có timeout nào — `sync-profile` đã vá 21/09 (docs/perf-boot.md §1.1)
// nhưng bước NÀY (chạy ngay sau, trước khi children render nội dung thật) thì chưa, RUM đo được
// `appReady` 87,9 GIÂY ở đúng tenant/máy đó.

export const NETWORK_RETRY_TIMEOUT_MS = 15_000;
// 2 lần tự thử lại (3 lượt gọi tổng) — cùng số với `lib/auth.tsx` AUTO_RETRY_DELAYS_MS, cho nhất quán
// trải nghiệm "chờ bao lâu thì mới thấy màn Thử lại" xuyên suốt các bước khởi động.
export const NETWORK_RETRY_DELAYS_MS = [1_500, 3_000];

class RetryTimeoutError extends Error {
  constructor() {
    super('network-retry: timed out waiting for promise');
    this.name = 'TimeoutError';
  }
}

function raceTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new RetryTimeoutError()), ms);
    p.then(
      v => { clearTimeout(t); resolve(v); },
      e => { clearTimeout(t); reject(e); },
    );
  });
}

/**
 * Mạng treo/mất kết nối (ĐÁNG thử lại — thường tự khỏi sau vài giây) hay lỗi THẬT (vd
 * `permission-denied` — thử lại vô ích, phải xử lý riêng ở nơi gọi). `unavailable`/`deadline-exceeded`
 * là mã lỗi Firestore SDK dùng khi không liên lạc được server (mất mạng/quá hạn), KHÔNG phải bị từ
 * chối quyền.
 */
export function isNetworkStallError(err: unknown): boolean {
  if (err instanceof RetryTimeoutError) return true;
  if (err instanceof TypeError) return true; // "Failed to fetch" — hiếm ở getDocs nhưng vẫn bắt cho chắc
  const code = (err as { code?: string } | null | undefined)?.code;
  return code === 'unavailable' || code === 'deadline-exceeded' || code === 'cancelled';
}

/**
 * @param onAttempt báo tiến độ (attempt kế tiếp, tổng số lần) để UI hiện animation "đang thử lại" —
 *   cùng vai trò với tham số cùng tên trong `lib/auth.tsx syncUserProfile`.
 */
export async function withNetworkRetry<T>(
  fn: () => Promise<T>,
  onAttempt?: (attempt: number, max: number) => void,
  timeoutMs: number = NETWORK_RETRY_TIMEOUT_MS,
  delaysMs: readonly number[] = NETWORK_RETRY_DELAYS_MS,
): Promise<T> {
  const maxAttempts = delaysMs.length + 1;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await raceTimeout(fn(), timeoutMs);
    } catch (err) {
      if (attempt === maxAttempts || !isNetworkStallError(err)) throw err;
      onAttempt?.(attempt + 1, maxAttempts);
      await new Promise(r => setTimeout(r, delaysMs[attempt - 1]));
    }
  }
  throw new Error('unreachable'); // vòng lặp trên luôn return hoặc throw trước khi tới đây
}
